import { createContext, useContext, useEffect, useState } from "react";


const CouponContextV2 = createContext();

export const useCouponContextV2 = () => useContext(CouponContextV2);

const STORAGE_KEY = "deliva-admin-active-coupon-details";

export const CouponProviderV2 = ({ children }) => {

  const initialState = {
    newCoupon: true,
    currentStep: 0,
    selectedCompany: null,
    status: 'draft', // Default status for new coupons
    couponDealOption: {
        first: { dealVal1: "", dealVal2: "", },
        second: { dealVal1: "", },
        third: { dealVal1: "", dealVal2: "", },
        couponExpiration: false, //  True if coupon doesn't expire, False if Expire
        expirationButton: 1,
        expirationOption: 1,
        description: "",
        selectedDealOptions: 0,
        extendExpiryTime: false, // New property for extending expiry time
    },
    couponDetails: {
      categories: [],
      couponClaimUrl: "",
      phoneNumber: "",
      emailAddress: "",
      countries: [],
      keywords: [],
      couponCode: "",
      isGlobal: false,
    },
    termsConditions: [
      { id: 1, instruction: 'Valid to new and existing customers.', checked: false },
      { id: 2, instruction: 'Valid to new customers only.', checked: false },
      { id: 3, instruction: 'NOT applicable to already discounted products and services.', checked: false },
    ],
    instructions: [
      { id: 1, instruction: 'Simply navigate to their website or online store.', checked: false },
      { id: 2, instruction: 'You must register online for this discount.', checked: false },
      { id: 3, instruction: "Install the vendor's mobile app.", checked: false },
      { id: 4, instruction: 'Enter this code on the online checkout page before payment.', checked: false },
      { id: 5, instruction: 'Show this code to the teller in-store.', checked: false },
      { id: 6, instruction: 'Present to salesperson after receiving a quote.', checked: false },
      { id: 7, instruction: 'Call for an appointment.', checked: false },
      { id: 8, instruction: 'Email for an appointment.', checked: false },
      { id: 9, instruction: 'WhatsApp for appointment.', checked: false },
    ],
    locationDetails: {
      locations: [],
      checkedId: [],
      onlineOnly: "no",
      callToActionButton: [
        { id: "navigateBtn", name: 'Navigate Button', checked: false },
        { id: "websiteBtn", name: 'Website Button', checked: false },
        { id: "callBtn", name: 'Call Button', checked: false },
        { id: "emailBtn", name: 'Email Button', checked: false }],
    },
    validFor: 7, // Store actual days instead of text
    timeframe: '1 weeks' // Store original timeframe selection
  };

  /**
   * Safely merge stored localStorage data with initialState defaults.
   * This ensures all required properties exist even if localStorage has old/incomplete data.
   * Preserves user's saved data while filling in any missing properties from defaults.
   */
  const getInitialState = () => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return initialState;

    try {
      const parsed = JSON.parse(stored);
      
      // Deep merge with defaults to ensure all nested properties exist
      return {
        ...initialState,
        ...parsed,
        // Merge nested objects to preserve their structure
        couponDealOption: {
          ...initialState.couponDealOption,
          ...(parsed.couponDealOption || {}),
          first: {
            ...initialState.couponDealOption.first,
            ...(parsed.couponDealOption?.first || {}),
          },
          second: {
            ...initialState.couponDealOption.second,
            ...(parsed.couponDealOption?.second || {}),
          },
          third: {
            ...initialState.couponDealOption.third,
            ...(parsed.couponDealOption?.third || {}),
          },
        },
        couponDetails: {
          ...initialState.couponDetails,
          ...(parsed.couponDetails || {}),
        },
        locationDetails: {
          ...initialState.locationDetails,
          ...(parsed.locationDetails || {}),
          // Ensure callToActionButton always has the correct structure
          callToActionButton: parsed.locationDetails?.callToActionButton?.length 
            ? parsed.locationDetails.callToActionButton 
            : initialState.locationDetails.callToActionButton,
        },
        // For arrays, use stored if exists and has items, otherwise use defaults
        termsConditions: parsed.termsConditions?.length 
          ? parsed.termsConditions 
          : initialState.termsConditions,
        instructions: parsed.instructions?.length 
          ? parsed.instructions 
          : initialState.instructions,
      };
    } catch (e) {
      console.error("Failed to parse localStorage coupon data, using defaults:", e);
      return initialState;
    }
  };

  const [couponState, setCouponState] = useState(getInitialState);


  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(couponState));
  }, [couponState]);


  const updateCouponState = (state) => {

    setCouponState((prevState) => ({
      ...prevState,
      ...state,
    }));
  }

  const clearState = () => {
    setCouponState(initialState);
    // Also clear from localStorage
    localStorage.removeItem(STORAGE_KEY);
  }

  const editCouponState = (companyDetails, couponDetails) => {
    console.log(couponDetails);
    const editState = {
      newCoupon: false,
      currentStep: couponDetails.step, // Use the step directly from API
      selectedCompany: companyDetails,
      couponId: couponDetails.id,
      createdAt: couponDetails.createdAt,
      status: couponDetails.status, // Add status field
      originalValidTill: couponDetails.validTill, // Store original validTill
      originalValidFor: couponDetails.validFor, // Store original validFor
      originalTimeframe: couponDetails.timeframe, // Store original timeframe
      isPopin: couponDetails.source === 'popin',
      couponDealOption: {
          first: { dealVal1: "", dealVal2: "", },
          second: { dealVal1: "", },
          third: { dealVal1: "", dealVal2: "", },
          couponExpiration: couponDetails.validFor === 99999 || couponDetails.source === 'popin', //  True if coupon doesn't expire, False if Expire
          description: couponDetails.description,
          selectedDealOptions: couponDetails.dealUnit === 'PER' ? 1 : (couponDetails.dealUnit === 'ITEM' || couponDetails.dealUnit === 'FREE') ? 0 : 2,
          extendExpiryTime: false, // New property for extending expiry time
          createdAt: couponDetails.createdAt,
          validTill: couponDetails.validTill,
          timeframe: couponDetails.timeframe || '1 weeks' // Store original timeframe in couponDealOption
      },
      couponDetails: {
        categories: couponDetails.categoryIds || [],
        couponClaimUrl: couponDetails.website || '',
        phoneNumber: couponDetails.mobile || '',
        emailAddress: couponDetails.email || '',
        countries: [],
        keywords: couponDetails.keywords?.split("|") || [],
        couponCode: couponDetails.coupon || '',
        isGlobal: false,
      },
      termsConditions: [
        { id: 1, instruction: 'Valid to new and existing customers.', checked: false },
        { id: 2, instruction: 'Valid to new customers only.', checked: false },
        { id: 3, instruction: 'NOT applicable to already discounted products and services.', checked: false },
      ],
      instructions: [
        { id: 1, instruction: 'Simply navigate to their website or online store.', checked: false },
        { id: 2, instruction: 'You must register online for this discount.', checked: false },
        { id: 3, instruction: "Install the vendor's mobile app.", checked: false },
        { id: 4, instruction: 'Enter this code on the online checkout page before payment.', checked: false },
        { id: 5, instruction: 'Show this code to the teller in-store.', checked: false },
        { id: 6, instruction: 'Present to salesperson after receiving a quote.', checked: false },
        { id: 7, instruction: 'Call for an appointment.', checked: false },
        { id: 8, instruction: 'Email for an appointment.', checked: false },
        { id: 9, instruction: 'WhatsApp for appointment.', checked: false },
      ],
      locationDetails: {
        locations: [],
        checkedId: couponDetails.locationIds || [],
        onlineOnly: couponDetails.onlineOnly,
        availabilityType: couponDetails.availabilityType,
        // Use availability from backend if available, otherwise derive it
        couponAvailability: couponDetails.availability || couponDetails.couponAvailability || (couponDetails.onlineOnly === "yes" && couponDetails.locationIds && couponDetails.locationIds.length > 0 
            ? "both" 
            : couponDetails.onlineOnly === "yes" 
              ? "online" 
              : "instore"),
        callToActionButton: [
          { id: "navigateBtn", name: 'Navigate Button', checked: couponDetails.navigateBtn === "yes" },
          { id: "websiteBtn", name: 'Website Button', checked: couponDetails.websiteBtn === "yes" },
          { id: "callBtn", name: 'Call Button', checked: couponDetails.callBtn === "yes" },
          { id: "emailBtn", name: 'Email Button', checked: couponDetails.emailBtn === "yes" }],
        timeframe: couponDetails.timeframe || '1 weeks', // Store original timeframe in couponDealOption
      },
      validFor: couponDetails.validFor ? parseInt(couponDetails.validFor) : 7, 
    };

  // Set deal values based on dealUnit
    if (couponDetails.dealUnit === 'PER') {
      editState.couponDealOption.second.dealVal1 = couponDetails.deal || '';
    } else if (couponDetails.dealUnit === 'ITEM') {
      // New structure: deal contains first input, dealItem contains second input
      editState.couponDealOption.first.dealVal1 = couponDetails.deal || '';
      editState.couponDealOption.first.dealVal2 = couponDetails.dealItem || '';
    } else if (couponDetails.dealUnit === 'FREE') {
      // Legacy structure: deal contains both values separated by space
      const dealValue = couponDetails.deal || '';
      const [val1, val2] = dealValue.split(' ');
      editState.couponDealOption.first.dealVal1 = val1 || '';
      editState.couponDealOption.first.dealVal2 = val2 || '';
    } else {
      editState.couponDealOption.third.dealVal1 = couponDetails.deal || '';
      editState.couponDealOption.third.dealVal2 = couponDetails.dealUnit ? couponDetails.dealUnit.toLowerCase() : '';
    }

    // Set terms and conditions
    let includedTerms = couponDetails.terms ? couponDetails.terms.split('|').map(t => t.trim()) : [];
    includedTerms.forEach(termText => {
      const existing = editState.termsConditions.find(t => t.instruction === termText);
      if (existing) {
        existing.checked = true;
      } else {
        editState.termsConditions.push({
          id: editState.termsConditions.length + 1,
          instruction: termText,
          checked: true
        });
      }
    });

    // Set instructions
    let includedInstructions = couponDetails.instructions ? couponDetails.instructions.split('|').map(i => i.trim()) : [];
    includedInstructions.forEach(instrText => {
      const existing = editState.instructions.find(i => i.instruction === instrText);
      if (existing) {
        existing.checked = true;
      } else {
        editState.instructions.push({
          id: editState.instructions.length + 1,
          instruction: instrText,
          checked: true
        });
      }
    });

    setCouponState(editState);
  }

  // const editCouponState = (newCouponData) => {
  //   console.log(newCouponData, "this is what we are passing here");

  //   var recievedCoupon = newCouponData.coupon;

  //   var editInitialState = {
  //     newCoupon: false,
  //     couponId: recievedCoupon.id,
  //     currentStep: 1,
  //     selectedCompany: newCouponData.selectedCompany,
  //     couponDealOption: {
  //       first: {
  //         dealVal1: "",
  //         dealVal2: "",
  //       },
  //       second: {
  //         dealVal1: "",

  //       },
  //       third: {
  //         dealVal1: "",
  //         dealVal2: "",

  //       },
  //       couponExpiration: recievedCoupon.validFor != 99999, //  True if coupon doesn't expire, False if Expire
  //       expirationButton: 1,
  //       expirationOption: 1,
  //       description: recievedCoupon.description,
  //       selectedDealOptions: 0,
  //     },
  //     couponDetailsPage: {
  //       categories: recievedCoupon.categories || [],
  //       couponClaimUrl: recievedCoupon.website || "",
  //       phoneNumber: recievedCoupon.mobile || "",
  //       emailAddress: recievedCoupon.email || "",
  //       countries: [],
  //       keywords: recievedCoupon.keywords?.split("|") || [],
  //       currentKeyword: "",
  //       couponCode: recievedCoupon.coupon,
  //     },
  //     currentTermsCondList: [
  //       { id: 1, instruction: 'Valid to new and existing customers.', checked: false },
  //       { id: 2, instruction: 'Valid to new customers only.', checked: false },
  //       { id: 3, instruction: 'NOT applicable to already discounted products and services.', checked: false },


  //     ],
  //     termCond: '',
  //     currinst: '',
  //     currentInstruction: [
  //       { id: 1, instruction: 'Simply navigate to their website or online store.', checked: false },
  //       { id: 2, instruction: 'You must register online for this discount.', checked: false },
  //       { id: 3, instruction: 'Install the vendor’s mobile app.', checked: false },
  //       { id: 4, instruction: 'Enter this code on the online checkout page before payment.', checked: false },
  //       { id: 5, instruction: 'Show this code to the teller in-store.', checked: false },
  //       { id: 6, instruction: 'Present to salesperson after receiving a quote.', checked: false },
  //       { id: 7, instruction: 'Call for an appointment.', checked: false },
  //       { id: 8, instruction: 'Email for an appointment.', checked: false },
  //       { id: 9, instruction: 'WhatsApp for appointment.', checked: false },

  //     ],
  //     locationDetailsPage: {
  //       locations: [],
  //       checkedId: [],
  //       onlineOnly: recievedCoupon.onlineOnly,
  //       callToActionButton: [
  //         { id: "navigateBtn", name: 'Navigate Button', checked: false },
  //         { id: "websiteBtn", name: 'Website Button', checked: false },
  //         { id: "callBtn", name: 'Call Button', checked: false },
  //         { id: "emailBtn", name: 'Email Button', checked: false },
  //       ]
  //     },
  //     validFor: `${couponState.couponDealOption.expirationOption} ${getExpirationOption(couponState.couponDealOption.expirationButton)}`
  //   };


  //   editInitialState.locationDetailsPage.callToActionButton.forEach(button => {

  //     switch (button.id) {
  //       case "navigateBtn":
  //         button.checked = recievedCoupon.navigateBtn && recievedCoupon.navigateBtn === "yes";
  //         break;
  //       case "websiteBtn":
  //         button.checked = recievedCoupon.websiteBtn && recievedCoupon.websiteBtn === "yes";
  //         break;
  //       case "callBtn":
  //         button.checked = recievedCoupon.callBtn && recievedCoupon.callBtn === "yes";
  //         break;
  //       case "emailBtn":
  //         button.checked = recievedCoupon.emailBtn && recievedCoupon.emailBtn === "yes";
  //         break;
  //       default:
  //         break;
  //     }
  //   })

  //   recievedCoupon.instructions?.split("|").forEach((instruction) => {

  //     const existingIndex = editInitialState.currentInstruction.findIndex((item) => item.instruction == instruction);

  //     if (existingIndex !== -1) {
  //       editInitialState.currentInstruction[existingIndex].checked = true;
  //     } else {
  //       editInitialState.currentInstruction.push({
  //         id: editInitialState.currentInstruction.length + 1,
  //         instruction: instruction,
  //         checked: true
  //       });
  //     }
  //   });

  //   recievedCoupon.terms?.split("|").forEach((terms) => {

  //     const existingIndex = editInitialState.currentTermsCondList.findIndex((item) => item.instruction == terms);

  //     if (existingIndex !== -1) {
  //       editInitialState.currentTermsCondList[existingIndex].checked = true;
  //     } else {
  //       editInitialState.currentTermsCondList.push({
  //         id: editInitialState.currentTermsCondList.length + 1,
  //         instruction: terms,
  //         checked: true
  //       });
  //     }
  //   });



  //   const getCurrentDealOption = (dealValue, dealType) => {

  //     switch (dealType) {

  //       case "PER":
  //         editInitialState.couponDealOption.selectedDealOptions = 1;
  //         editInitialState.couponDealOption.second.dealVal1 = dealValue;
  //         break;
  //       default:
  //         break;
  //     }

  //   }


  //   getCurrentDealOption(recievedCoupon.deal, recievedCoupon.dealUnit);
  //   setCouponState(editInitialState);
  // }

  return <CouponContextV2.Provider value={{ couponState, updateCouponState, clearState, editCouponState }}>{children}</CouponContextV2.Provider>

}